import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TableRowUnSelectEvent } from 'primeng/table';
import { MatDialog } from '@angular/material/dialog';
import { zip } from 'rxjs';

import { LancamentoPrincipal } from '../../../models/lancamento-principal';
import { ConciliacaoApiService } from '../../../services/conciliacao-api.service';
import { ConciliacaoService } from '../../../services/conciliacao.service';
import { MessageService } from '@services/message.service';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';
import { TransferirLancamentoDialogComponent } from '@components/transferir-lancamento-dialog/transferir-lancamento-dialog.component';
import { Lancamento } from '../../../models/lancamento';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ExibeCorrespondenciaDialogComponent } from '../../../../../../shared/dialogs/exibe-correspondencia-dialog/exibe-correspondencia-dialog.component';
import { InfoRegistroModel } from '@models/info-registro.model';
import { LancamentoApiService } from '@services/lancamento-api.service';
import { PermissionService } from '@services/permission.service';
import { Permissao } from '@models/enums/permissao';
import { RegraConciliacaoAutomaticaDialogComponent } from '../../../../regra-conciliacao-automatica/components/dialogs/regra-conciliacao-automatica-dialog/regra-conciliacao-automatica-dialog.component';
import { ConfiguracaoCaixaApiService } from '../../../../configuracao-caixa/services/configuracao-caixa-api.service';
import { TipoLancamentoApiService } from '@services/tipo-lancamento-api.service';
import { CadastroLoteLancamentosDialogComponent, CadastroLoteLancamentosDialogData } from '../../../../../../shared/dialogs/cadastro-lote-lancamentos-dialog/cadastro-lote-lancamentos-dialog.component';

@Component({
  selector: 'app-lancamentos-table',
  standalone: false,
  templateUrl: './lancamentos-table.component.html',
  styleUrl: './lancamentos-table.component.scss'
})
export class LancamentosTableComponent {

  protected get permissaoEditarLancamento() { return Permissao.EDITAR_LANCAMENTO_CAIXA; }
  protected get permissaoEditarRegra() { return Permissao.EDITAR_REGRA_CONCILIACAO_AUTOMATICA; }
  @Input() lancamentosExibidos: LancamentoPrincipal[] = [];
  @Input() ehTabelaPrincipal: boolean;
  @Input() cardEhBanco: boolean = false;
  @Input() modoEdicao: boolean;
  @Input() autoMultiSelect: boolean = false;
  @Input() conciliacaoId: number;
  
  @Input() lancamentoAgrupador: LancamentoPrincipal | undefined = undefined;

  @Input() selectedRows: LancamentoPrincipal[] = [];
  @Output() selectedRowsChange = new EventEmitter<LancamentoPrincipal[]>();

  @Output() rowSelect = new EventEmitter<any>();
  @Output() rowUnselect = new EventEmitter<any>();
  @Output() rowDelete = new EventEmitter<any>();

  expandedRows: any = {};

  lancamentosClone: { [s: number]: LancamentoPrincipal } = {};
  editingRowKeys: { [key: string]: boolean } = {};

  disableSalvarObservacaoBanco: boolean;
  disableSalvarObservacaoCaixa: boolean;

  constructor(
    private conciliacaoApiService: ConciliacaoApiService,
    private conciliacaoService: ConciliacaoService,
    private messageService: MessageService,
    private dialog: MatDialog,
    private lancamentoApiService: LancamentoApiService,
    private permissionService: PermissionService,
    private configuracaoCaixaApiService: ConfiguracaoCaixaApiService,
    private tipoLancamentoApiService: TipoLancamentoApiService,
  ) {
    this.disableSalvarObservacaoBanco = !this.permissionService.has(Permissao.EDITAR_OBSERVACAO_LANC_BANCO);
    this.disableSalvarObservacaoCaixa = !this.permissionService.has(Permissao.EDITAR_LANCAMENTO_CAIXA);
  }

  public onChildSelectionChange(rows: LancamentoPrincipal[]) {
    this.selectedRows = rows;
    this.onUpdateRowSelection();
  }

  private onUpdateRowSelection(): void {
    this.selectedRowsChange.emit([...this.selectedRows]);
  }

  private unselectChildren(lancamento: LancamentoPrincipal): void {
    if (!lancamento.ehAgrupador) return;

    lancamento.lancamentosAgrupados?.forEach(l => this.unselectLancamentoById(l.id));
  }

  private unselectLancamentoById(id: number): void {
    this.selectedRows = this.selectedRows.filter(x => x.id !== id);
  }

  private editSave(lancamento: LancamentoPrincipal): void {
    const info = this.conciliacaoService.getInformacoesSalvarConciliacao();
    const novoValor = lancamento.valor;

    this.conciliacaoApiService.salvarConciliacao(info).subscribe((dados) => {
      // Sincroniza com o retorno do save para não perder lançamentos/correspondências criados pelas regras
      this.conciliacaoService.setDadosConciliacao(dados);

      this.conciliacaoApiService.editarLancamento(dados.conciliacaoId, lancamento).subscribe(() => {
        this.messageService.showToastSuccess("Salvo com sucesso.");

        const lancamentoAtualizado = this.conciliacaoService.lancamentosCaixaMap.get(lancamento.id);
        if (lancamentoAtualizado) {
          lancamentoAtualizado.valor = novoValor;
          lancamentoAtualizado.valorX100 = novoValor * 100;
        }
      });
    });
  }

  public onRowUnselect(event: TableRowUnSelectEvent<any>): void {
    const pointerEvent: PointerEvent | undefined = event.originalEvent as PointerEvent | undefined;
    // Esse metodo é chamado uma vez para cada linha selecionada, antes do onRowSelect ser chamado quando é usado o shift para multiseleção
    if (pointerEvent !== undefined && !pointerEvent.shiftKey)
      this.onUpdateRowSelection();
  }

  public onRowSelect(row: LancamentoPrincipal | LancamentoPrincipal[] | undefined): void {
    if (row === undefined) return;

    if (this.lancamentoAgrupador === undefined) {
      if (Array.isArray(row)) row.forEach(r => this.unselectChildren(r));
      else this.unselectChildren(row);
    }
    else {
      this.unselectLancamentoById(this.lancamentoAgrupador.id);
    }
    this.onUpdateRowSelection();
  }

  public onRowEditInit(lancamento: LancamentoPrincipal) {
    this.lancamentosClone[lancamento.id] = { ...lancamento };
    this.editingRowKeys[lancamento.id] = true;
  }

  public onRowDeleteClick(lancamento: LancamentoPrincipal) {
    if (lancamento.conciliado) {
      this.messageService.showToastWarning("Não é possivel deletar lançamentos conciliados.");
      return;
    }

    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      autoFocus: false
    });
    dialogRef.componentInstance.titulo = "Excluir lançamento";
    dialogRef.componentInstance.mensagem = `Essa ação não pode ser desfeita`;
    dialogRef.componentInstance.mensagemBold = 'Deseja continuar?';
    dialogRef.componentInstance.acaoConfirmacao = this.excluirLancamento.bind(this, lancamento.id);
    dialogRef.componentInstance.textoBtnConfirmacao = "Excluir";
    dialogRef.componentInstance.btnBackgroundColor = "#F44336";
  }

  private excluirLancamento(lancamentoId: number): void {
    this.conciliacaoApiService.deleteLancamento(lancamentoId, this.conciliacaoId).subscribe(() => {
      this.messageService.showToastSuccess("Excluido com sucesso.");
      this.removerLancamentoDaTela(lancamentoId)
    })
  }

  public onAlterarContaClick(lancamento: LancamentoPrincipal) {
    if (lancamento.conciliado) {
      this.messageService.showToastWarning("Não é possivel alterar a conta de lançamentos conciliados.");
      return;
    }

    const dialogRef = this.dialog.open(TransferirLancamentoDialogComponent, {
      width: '580px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
      data: {
        lancamentoIds: [lancamento.id],
        conciliacaoId: this.conciliacaoId,
        tipoLancamentoIdAtual: lancamento.tipoLancamentoId,
      },
    });

    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo) {
        this.removerLancamentoDaTela(lancamento.id);
      }
    });
  }

  public onRowEditSave(lancamento: LancamentoPrincipal): void {
    if (lancamento.ehAgrupador) {
      this.messageService.showToastWarning("Não é possível editar agrupadores");
      this.onRowEditCancel(lancamento);
    }
    else if (this.lancamentosClone[lancamento.id].valor === lancamento.valor)
      return;
    else if (lancamento.conciliado) {
      this.messageService.showToastWarning("Não é permitido editar o valor de lançamentos conciliados");
    }
    else {
      this.editSave(lancamento);
    }
  }

  public onRowEditCancel(lancamento: LancamentoPrincipal): void {
    const lancamentoAntes = this.lancamentosClone[lancamento.id];
    lancamento.data = lancamentoAntes.data;
    lancamento.descricao = lancamentoAntes.descricao;
    lancamento.valor = lancamentoAntes.valor;
    lancamento.valorX100 = lancamentoAntes.valorX100;
    delete this.lancamentosClone[lancamento.id];
  }

  private removerLancamentoDaTela(id: number): void {
    this.selectedRows = this.selectedRows.filter(x => x.id !== id);
    this.onUpdateRowSelection();
  
    this.conciliacaoService.removerLancamento(id, this.cardEhBanco);
  }

  public onCheckboxClick(event: MatCheckboxChange, lancamento: Lancamento): void {
    // desfaz a mudança
    event.source.checked = lancamento.conciliado;
    
    if (!lancamento.conciliado) {
      this.conciliacaoService.conciliarCheckBoxClick(this.cardEhBanco, lancamento);
    }
    else {
      this.openExibirCorrespondenciaDialog(lancamento, true);
    }
  }

  public visualizarCorrespondenciaClick(lancamento: Lancamento): void {
    this.openExibirCorrespondenciaDialog(lancamento, false);
  }

  private openExibirCorrespondenciaDialog(lancamento: Lancamento, acaoDesfazer: boolean): void {
    const correspondenciaIndex = this.conciliacaoService.getCorrespondenciaIndex(lancamento, this.cardEhBanco);

    const dialogRef = this.dialog.open(ExibeCorrespondenciaDialogComponent, {
      width: '1000px',
      maxWidth: '100vw',
      panelClass: 'no-padding-dialog',
      autoFocus: false,
      disableClose: acaoDesfazer,
      data: {
        acaoDesfazer: acaoDesfazer,
        correspondenciaIndex: correspondenciaIndex,
      }
    });
  }

  public editarObservacao(lancamento: InfoRegistroModel) {
    console.log('test')
    this.lancamentoApiService.editarObservacao(lancamento, this.cardEhBanco).subscribe(() => {
      this.messageService.showToastSuccess("Salvo com sucesso")
    });
  }

  public onCriarRegraClick(lancamento: LancamentoPrincipal): void {
    const contaCaixaId = this.conciliacaoService.dadosConciliacao.contaCaixaId;

    zip(
      this.configuracaoCaixaApiService.getTiposDaConta(contaCaixaId),
      this.tipoLancamentoApiService.getTiposLancamento(),
    ).subscribe(([tiposOrigem, tipos]) => {
      const dialogRef = this.dialog.open(RegraConciliacaoAutomaticaDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        panelClass: 'no-padding-dialog',
        disableClose: true,
      });
      dialogRef.componentInstance.contaCaixaId = contaCaixaId;
      dialogRef.componentInstance.regra = null;
      dialogRef.componentInstance.tiposOrigem = tiposOrigem;
      dialogRef.componentInstance.tiposDestino = tipos.map(t => ({ id: t.id, nome: `${t.nome} (${t.nomeConta})` }));
      dialogRef.componentInstance.termoBuscaInicial = lancamento.descricao;
    });
  }

  public onAdicionarLoteCorrespondenteClick(lancamento: LancamentoPrincipal): void {
    const dialogRef = this.dialog.open(CadastroLoteLancamentosDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: true,
      data: {
        data: new Date(lancamento.data),
        valorLancamentoBanco: lancamento.valor,
        contaCaixaId: this.conciliacaoService.dadosConciliacao.contaCaixaId,
      } as CadastroLoteLancamentosDialogData,
    });

    dialogRef.afterClosed().subscribe((salvo: boolean | undefined) => {
      if (salvo) this.conciliacaoService.recarregarLancamentosCaixaAposAdicionarLoteCorrespondente();
    });
  }
}
